export default class CompliantConsentManager {
    constructor() {
        this.queue = [];
        this.essentialCookieName = "tessellate_necessary_consent";

        const consent = this.getConsentFromCookie();
        this.consentGranted =
            !!consent && Object.values(consent).some((v) => v === "granted");
            
        this.savedConsent = consent || {
            ad_storage: "denied",
            analytics_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            meta: "denied",
        };

        this.initializeConsent();
    }

    getConsentFromCookie() {
        const match = document.cookie
            .split("; ")
            .find((row) => row.startsWith(`${this.essentialCookieName}=`));

        if (!match) return null;

        try {
            const jsonStr = decodeURIComponent(match.split("=")[1]);
            return JSON.parse(jsonStr);
        } catch (e) {
            console.warn("Consent cookie is invalid:", e);
            return null;
        }
    }

    setConsentCookie(consent) {
        const date = new Date();
        date.setFullYear(date.getFullYear() + 1);
        const cookieValue = encodeURIComponent(JSON.stringify(consent));
        document.cookie = `${this.essentialCookieName}=${cookieValue}; expires=${date.toUTCString()}; path=/; SameSite=Lax`;
    }

    initializeConsent() {
        // NOTE: We do NOT call gtag("consent", "default") here.
        // That is handled exclusively by the inline script in consent.php
        // (fired at wp_head priority 0), which runs before GTM/GA4 loads.
        // Calling it again here would send a duplicate default signal and
        // can cause GA4 to discard or mishandle the subsequent update call.

        const cookieConsent = this.getConsentFromCookie();

        if (cookieConsent) {
            this.savedConsent = cookieConsent;

            // Sync checkbox states to match the saved cookie.
            // We handle both checked and unchecked so that a user who
            // previously denied a category doesn't see a stale checked toggle.
            const analyticsEl = document.getElementById("analytics_storage");
            if (analyticsEl) {
                analyticsEl.checked = cookieConsent.analytics_storage === "granted";
            }

            const adStorageEl = document.getElementById("ad_storage");
            if (adStorageEl) {
                adStorageEl.checked = cookieConsent.ad_storage === "granted";
            }

            const metaEl = document.getElementById("meta");
            if (metaEl) {
                metaEl.checked = cookieConsent.meta === "granted";
            }
        }

        if (this.consentGranted) {
            this.processQueue();
        }
    }

    grantConsent() {
        if (this.consentGranted) return;

        const wasAnalyticsGranted =
            this.savedConsent.analytics_storage === "granted";

        const newConsent = {
            ad_storage: "granted",
            analytics_storage: "granted",
            ad_user_data: "granted",
            ad_personalization: "granted",
            meta: "granted",
        };

        if (site_data.meta === "yes") {
            if (window.pys) {
                window.pys.Facebook.loadPixel();
            }
        }

        this.savedConsent = newConsent;
        this.setConsentCookie(newConsent);

        window.gtag("consent", "update", newConsent);
        this.processQueue();
        this.consentGranted = true;

        // Trigger page view if analytics consent was newly granted
        if (!wasAnalyticsGranted) {
            this.triggerPageView();
        }

        const banner = document.getElementById("tessellate-consent-banner");
        if (banner) banner.style.display = "none";

        // Sync checkboxes to reflect granted state
        const analyticsEl = document.getElementById("analytics_storage");
        if (analyticsEl) analyticsEl.checked = true;

        const adStorageEl = document.getElementById("ad_storage");
        if (adStorageEl) adStorageEl.checked = true;

        if (site_data.meta === "yes") {
            const metaEl = document.getElementById("meta");
            if (metaEl) metaEl.checked = true;
        }
    }

    updateConsent(consentUpdates) {
        if (typeof consentUpdates !== "object" || consentUpdates === null) {
            throw new Error("Consent updates must be an object.");
        }

        const wasAnalyticsGranted =
            this.savedConsent.analytics_storage === "granted";

        const updatedConsent = { ...this.savedConsent };

        for (const [key, value] of Object.entries(consentUpdates)) {
            if (value === "granted" || value === "denied") {
                updatedConsent[key] = value;
            } else {
                console.warn(`Invalid consent value for ${key}: ${value}`);
            }
        }

        this.savedConsent = updatedConsent;
        this.setConsentCookie(updatedConsent);

        window.gtag("consent", "update", updatedConsent);

        this.consentGranted = Object.values(updatedConsent).some(
            (v) => v === "granted"
        );

        // Only process queue if consent is now granted
        if (this.consentGranted) {
            this.processQueue();
        }

        if (site_data.meta === "yes") {
            if (updatedConsent.meta === "granted" && window.pys) {
                window.pys.Facebook.loadPixel();
                const metaEl = document.getElementById("meta");
                if (metaEl) metaEl.checked = true;
            }
        }

        // Sync checkboxes to reflect granted state
        if (updatedConsent.analytics_storage === "granted"){
            const analyticsEl = document.getElementById("analytics_storage");
            if (analyticsEl) analyticsEl.checked = true;
        }

        if (updatedConsent.ad_storage === "granted"){
            const adStorageEl = document.getElementById("ad_storage");
            if (adStorageEl) adStorageEl.checked = true;
        }

        if (site_data.meta === "yes") {
            const metaEl = document.getElementById("meta");
            if (metaEl) metaEl.checked = true;
        }

        // Trigger page view if analytics consent was newly granted
        const isAnalyticsGrantedNow = updatedConsent.analytics_storage === "granted";
        if (!wasAnalyticsGranted && isAnalyticsGrantedNow) {
            this.triggerPageView();
        }
    }

    revokeConsent() {
        this.consentGranted = false;

        // FIX: meta key is now included so the cookie schema stays consistent
        // across all methods. Previously revokeConsent dropped the meta key,
        // which meant a subsequent read of the cookie could leave meta undefined
        // rather than explicitly "denied".
        this.savedConsent = {
            ad_storage: "denied",
            analytics_storage: "denied",
            ad_user_data: "denied",
            ad_personalization: "denied",
            meta: "denied",
        };

        document.cookie = `${this.essentialCookieName}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;

        window.gtag("consent", "update", this.savedConsent);

        const banner = document.getElementById("tessellate-consent-banner");
        if (banner) banner.style.display = "none";

        // Sync checkboxes to reflect denied state
        const analyticsEl = document.getElementById("analytics_storage");
        if (analyticsEl) analyticsEl.checked = false;

        const adStorageEl = document.getElementById("ad_storage");
        if (adStorageEl) adStorageEl.checked = false;

        if (site_data.meta === "yes") {
            const metaEl = document.getElementById("meta");
            if (metaEl) metaEl.checked = false;
        }

        this.queue = [];
    }

    sendEvent(eventName, eventParams = {}) {
        const eventData = {
            event_name: eventName,
            ...eventParams,
        };

        if (this.consentGranted) {
            window.gtag("event", eventName, eventParams);
        } else {
            this.queue.push(eventData);
        }
    }

    processQueue() {
        while (this.queue.length > 0) {
            const event = this.queue.shift();
            window.gtag("event", event.event_name, event);
        }
    }

    triggerPageView() {
        window.gtag("event", "page_view", {
            page_title: document.title,
            page_location: window.location.href,
            page_path: window.location.pathname,
            send_to: site_data.analytics,
        });
    }
}